import type { TourStep } from '@/components/ui/Tour';
import {
  CLUB_PAGE_TOUR_STEP_IDS,
  MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS,
  TOUR_CENTERED_STEP_SELECTOR,
  TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
} from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface ClubMembersTourOptions {
  onEnterMembers?: () => void;
  onEnterMemberships?: () => void;
  onEnterTypes?: () => void;
  onEnterAddons?: () => void;
  onEnterSettings?: () => void;
  onOpenCreateType?: () => void;
  onCloseCreateType?: () => void;
  onFinish?: () => void;
}

const openCreateTypeDrawer = (options: ClubMembersTourOptions) => () => {
  options.onEnterTypes?.();
  options.onOpenCreateType?.();
};

function membershipTypeCreateDrawerStep(
  onStepEnter?: () => void,
): Pick<TourStep, 'onStepEnter' | 'targetPollTimeoutMs' | 'waitForPanelReady'> {
  return {
    onStepEnter,
    targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
    waitForPanelReady: true,
  };
}

export function getMembershipTypeCreateFormTourSteps(onStepEnter?: () => void): TourStep[] {
  const drawerStep = membershipTypeCreateDrawerStep(onStepEnter);

  return [
    {
      content: (
        <TourStepContent
          title="Create a membership plan"
          description="Work through each section — basics, term, seats, eligibility, and visibility — then save."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.DRAWER,
      position: 'left',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Plan basics"
          description="Name the plan, add an optional description, set the price, and configure a court hire discount for members."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.DETAILS,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Term"
          description="Choose recurring billing (monthly or yearly) or a fixed period tied to your club season or join date."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.TERM,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Covered seats"
          description="Set how many adults and juniors the plan covers — use 0 adults for junior-only, or multiple seats for couple and family plans."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.SEATS,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Eligibility"
          description="Enforce age limits, require proof for concession plans, and control playing vs social or parent-and-junior rules."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.ELIGIBILITY,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Visibility"
          description="Show the plan on your public join page and keep it active so staff can assign it to new members."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.VISIBILITY,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Save the plan"
          description="Create the plan to assign or sell memberships from the roster."
        />
      ),
      selectorId: MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.SUBMIT,
      position: 'top',
      ...drawerStep,
    },
  ];
}

export function getClubMembersTourSteps(options: ClubMembersTourOptions = {}): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Members tab"
          description="Everyone registered at your club appears here. Search the roster, assign memberships, and send notices."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_MEMBERS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterMembers?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Memberships tab"
          description="Active and past memberships across your club. Assign plans, track payment status, and manage renewals."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_MEMBERSHIPS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterMemberships?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Membership plans tab"
          description="Plans define pricing, seat limits, and entitlements like court hire discounts."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_TYPES,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterTypes?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="New membership plan"
          description="Add a plan here — we'll walk through the create form next."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES_NEW,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterTypes?.();
      },
    },
    ...getMembershipTypeCreateFormTourSteps(openCreateTypeDrawer(options)).map((step) =>
      step.selectorId === MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.SUBMIT
        ? {
            ...step,
            content: (
              <TourStepContent
                title="Save the plan"
                description="Create the plan to assign or sell memberships from the roster. You can close without saving to continue the tour."
              />
            ),
          }
        : step,
    ),
    {
      content: (
        <TourStepContent
          title="Add-ons tab"
          description="Optional extras members can purchase with a plan, such as locker hire, ball machine access, or league competitions."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_ADDONS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterAddons?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Settings tab"
          description="Configure online join, membership year boundaries, and club-wide membership options."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_SETTINGS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterSettings?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Club membership options"
          description="Enable self-service join on your public site when Stripe Connect is ready."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_SETTINGS,
      position: 'top',
      onStepEnter: () => {
        options.onCloseCreateType?.();
        options.onEnterSettings?.();
      },
    },
  ];
}

/** @deprecated Use getClubMembersTourSteps for the unified page tour. */
export const clubMembersRosterTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Member roster"
        description="Everyone registered at your club appears here. Assign memberships, send notices, and manage household seats."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Members list"
        description="Search and filter the roster. Open a member to view their memberships, contact details, and household."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_ROSTER,
    position: 'top',
  },
];

/** @deprecated Use getClubMembersTourSteps for the unified page tour. */
export const clubMembersTypesTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Membership plans"
        description="Plans define pricing, seat limits, and entitlements like court hire discounts. Members join a plan to hold an active seat."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Plan list"
        description="Create recurring or fixed-period plans. Control who can self-join online versus staff-assigned only."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="New membership plan"
        description="Add your first plan here — we'll tour the create form when you open it."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES_NEW,
    position: 'bottom',
  },
];

/** @deprecated Use getClubMembersTourSteps for the unified page tour. */
export const clubMembersSettingsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Membership settings"
        description="Configure online join, membership year boundaries, and club-wide membership options."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Club membership options"
        description="Enable self-service join on your public site when Stripe Connect is ready."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_SETTINGS,
    position: 'top',
  },
];

export const membershipTypeCreateTourSteps: TourStep[] = getMembershipTypeCreateFormTourSteps();
